import {
  toPercent,
  toMoney,
  abreviarValor,
  getRandomColor,
  abreviarMonetario,
  toDecPercent,
} from '@utils/Format'
import store from '@state/store'
import {
  paleta,
  donutChartColors,
  commissionsChartColor,
  _paleta,
} from '@constants/Color'
import {
  getLegendEndOfLinePlugin,
  getBarValueLabelsPlugin,
} from '@plugins/chartjs-plugins.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { createGoalMarkersPlugin } from '@plugins/chartjs-goal-markers.js'
import Chart from 'chart.js'

// Estilo compartilhado dos gráficos do dashboard no Novo Design 2026 (canvas não
// lê CSS var, então os tokens entram como hex): fonte de dados Geist, texto
// neutro (#8E8E8E, mesma cor de ícones/aux), grade hairline (--border-hairline)
// e legenda enxuta.
const AXIS_FONT_FAMILY = 'Geist'
const AXIS_FONT_COLOR = '#8E8E8E'
const GRID_COLOR = '#ECECEF'
const LEGEND_BOX_WIDTH = 12

const abreviarValorMask = (v) => {
  return `R$ ${abreviarValor(v)}`
}

/**
 * Chart.js v2 tooltip default callbacks.
 *
 * When `options.tooltips.callbacks` is replaced wholesale, Chart.js loses its
 * built-in defaults for `beforeTitle`, `title`, `afterTitle`, `beforeBody`,
 * `afterBody`, `beforeFooter`, `footer` and `afterFooter`. On `mousemove`,
 * the internal `_.getTitle` does `callbacks.beforeTitle.apply(...)` and
 * `_.draw` reads `model.title.length` — both crash with "Cannot read
 * properties of undefined" when those callables are missing.
 *
 * See AAWZ-WEB-3W3 / AAWZ-WEB-3W2 (Sentry, post-release 4.133.0).
 */
const defaultTitleCallback = (items) => {
  if (!items || !items.length) return ''
  const first = items[0]
  if (!first) return ''
  if (first.xLabel != null) return first.xLabel
  if (first.label != null) return first.label
  return ''
}

/**
 * Wraps a partial Chart.js v2 tooltip callbacks object with explicit defaults
 * so the Chart.js contract holds even when only `label` / `title` are
 * customized. Always pass user callbacks last so they win.
 */
export const withTooltipDefaults = (callbacks = {}) => {
  return {
    beforeTitle: () => [],
    title: defaultTitleCallback,
    afterTitle: () => [],
    beforeBody: () => [],
    afterBody: () => [],
    beforeFooter: () => [],
    footer: () => [],
    afterFooter: () => [],
    ...callbacks,
  }
}

export const getDatasetColor = (key) => {
  const normalizedKey = key.toLowerCase()
  const Colors = store.getters['parameters/colors'] || {}

  if (normalizedKey.includes('escritório')) {
    return Colors.primary
  }
  if (normalizedKey.includes('mínimo garantido')) {
    return commissionsChartColor.guaranteedMinimum
  }
  if (normalizedKey.includes('receita')) {
    return commissionsChartColor.revenue
  }
  if (normalizedKey.includes('comissão')) {
    return commissionsChartColor.commission
  }

  return donutChartColors[4]
}

/**
 * Retorna as opções de formatação de dados a partir de um parâmetro atualmente podendo ser "R$" || "%"
 */
export const optionsFormatData = (mode, currency = 'real') => {
  const normalizedCurrency = currency || 'real'
  const abreviarMonetarioWithCurrency = (v) =>
    abreviarMonetario(v, undefined, normalizedCurrency)
  const toMoneyWithCurrency = (v) => toMoney(v, 2, normalizedCurrency)

  const funcTypesY = {
    '%': toPercent,
    R$: abreviarValorMask,
    monetario: abreviarMonetarioWithCurrency,
    monetario_abreviado: abreviarMonetarioWithCurrency,
    porcentagem: toPercent,
    porcentagem_decimal: toDecPercent,
    numero: (v) => v,
  }
  const funcTypesTooltip = {
    '%': toPercent,
    R$: toMoneyWithCurrency,
    monetario: toMoneyWithCurrency,
    monetario_abreviado: toMoneyWithCurrency,
    porcentagem: toPercent,
    porcentagem_decimal: toDecPercent,
    numero: (v) => v,
  }
  const funcFormatData = (v) => {
    return v
  }

  const formatValuesY = funcTypesY[mode] || funcFormatData
  const formatValuesTooltip = funcTypesTooltip[mode] || funcFormatData

  return {
    formatValuesY,
    formatValuesTooltip,
    funcFormatData,
  }
}

// Tooltip do gráfico de linha: resolve o modo (%/R$) por dataset (permite
// mistura de modos no mesmo gráfico via item.modeRightAxis/item.mode),
// caindo no modo geral de `dados` quando o dataset não especifica o dele.
const buildLinesTooltipLabel = (dados) => (tooltipItems, data) => {
  const dataset = data.datasets[tooltipItems.datasetIndex]
  const value = optionsFormatData(
    dataset.modeRightAxis || dataset.mode || dados.mode || 'R$'
  ).formatValuesTooltip(tooltipItems.yLabel)
  return `${dataset.label}: ${value}`
}

// Eixo Y esquerdo (sempre presente) + eixo Y direito (só quando algum
// dataset declara `axis: 'right-axis'` — ex: uma métrica em % ao lado de
// outra em R$).
const buildLinesYAxes = (dados) => {
  const { formatValuesY, funcFormatData } = optionsFormatData(dados.mode)
  const formatData = dados.formatData || funcFormatData
  const hasRightAxis = dados.data.find((item) => item.axis === 'right-axis')

  return [
    {
      id: 'left-axis',
      position: 'left',
      gridLines: {
        color: GRID_COLOR,
        zeroLineColor: GRID_COLOR,
        drawBorder: false,
      },
      ticks: {
        fontSize: 12,
        fontColor: AXIS_FONT_COLOR,
        fontStyle: 'normal',
        fontFamily: AXIS_FONT_FAMILY,
        callback: (v, i, vs) => {
          return formatValuesY(formatData(v))
        },
      },
    },
    hasRightAxis && {
      id: 'right-axis',
      position: 'right',
      ticks: {
        display: true,
        beginAtZero: true,
        fontSize: 12,
        fontColor: AXIS_FONT_COLOR,
        fontStyle: 'normal',
        fontFamily: AXIS_FONT_FAMILY,
        callback: (v, i, vs) => {
          return optionsFormatData(
            hasRightAxis.modeRightAxis || 'R$'
          ).formatValuesY(formatData(v))
        },
      },
      gridLines: {
        display: true,
        color: '#ECECEC99',
        drawOnChartArea: false,
      },
    },
  ].filter((axis) => !!axis)
}

const buildLinesXAxes = (legendEndOfLine) => [
  {
    padding: 10,
    gridLines: {
      display: false,
    },
    offset: legendEndOfLine,
    ticks: {
      fontSize: 12,
      fontColor: AXIS_FONT_COLOR,
      fontStyle: 'normal',
      fontFamily: AXIS_FONT_FAMILY,
    },
  },
]

const buildLinesChartOptions = (dados, legendEndOfLine) => ({
  responsive: true,
  maintainAspectRatio: false,
  tooltips: {
    enabled: true,
    mode: 'index',
    intersect: false,
    callbacks: withTooltipDefaults({
      label: buildLinesTooltipLabel(dados),
    }),
  },
  hover: {
    mode: 'index',
    intersect: false,
  },
  scales: {
    yAxes: buildLinesYAxes(dados),
    xAxes: buildLinesXAxes(legendEndOfLine),
  },
  elements: {
    point: {
      radius: 0.1,
      borderWidth: 0,
      style: 'circle',
    },
    line: {
      borderWidth: 0.5,
    },
  },
  legend: {
    display: !legendEndOfLine,
    position: 'bottom',
    align: 'start',
    labels: {
      usePointStyle: false,
      boxWidth: LEGEND_BOX_WIDTH,
      padding: 12,
      fontSize: 11,
    },
  },
})

const buildLinesDatasets = (dados, Colors) =>
  dados.data.map((item) => ({
    label: item.label,
    fill: false,
    borderColor: item.color || Colors.primary,
    backgroundColor: item.color || Colors.primary,
    borderWidth: 3,
    data: item.dados || [],
    type: 'line',
    pointStyle: 'circle',
    yAxisID: item.axis || 'left-axis',
    mode: item.modeRightAxis || item.mode || dados.mode,
    ...(item.chartOptions || {}),
  }))

// getLegendEndOfLinePlugin (chartjs-plugins.js) desenha o label de cada linha
// logo após o último ponto, deslocado por offsetX (10px). Sem espaço
// reservado à direita do chartArea, esse texto é cortado pela borda do
// canvas. Reserva largura estimada (heurística de largura média por
// caractere p/ 12px Arial — mesma fonte de drawLabel, sem canvas/document
// para não depender de polyfill em ambiente de teste) + o offset do plugin
// + margem de segurança.
const buildLegendEndOfLineLayout = (chartOptions, datasets) => {
  const END_OF_LINE_OFFSET_X = 10
  const AVERAGE_CHAR_WIDTH_PX = 7
  const SAFETY_MARGIN_PX = 8
  const widestLabelChars = datasets.reduce(
    (max, ds) => Math.max(max, (ds.label || '').length),
    0
  )
  const existingPadding =
    chartOptions.layout && chartOptions.layout.padding
      ? chartOptions.layout.padding
      : {}
  return {
    ...chartOptions.layout,
    padding: {
      ...existingPadding,
      right:
        widestLabelChars * AVERAGE_CHAR_WIDTH_PX +
        END_OF_LINE_OFFSET_X +
        SAFETY_MARGIN_PX,
    },
  }
}

const hasUsableGoals = (dados) =>
  !!dados.applyGoalMarkers &&
  Array.isArray(dados.goals) &&
  !!dados.goals.length

// Padding mínimo (top/bottom) pra acomodar os ícones de meta acima/abaixo da
// linha — respeita o padding vertical já existente (número único vira
// {top,bottom} iguais) e nunca reduz um valor maior já configurado.
const computeGoalMarkersPadding = (existingLayout, goalMarkerOptions) => {
  const layoutPadding = existingLayout ? existingLayout.padding : undefined
  const normalizedPadding =
    typeof layoutPadding === 'number'
      ? { top: layoutPadding, bottom: layoutPadding }
      : { ...(layoutPadding || {}) }

  const minTopPadding =
    goalMarkerOptions.minTopPadding !== undefined
      ? goalMarkerOptions.minTopPadding
      : 32
  const minBottomPadding =
    goalMarkerOptions.minBottomPadding !== undefined
      ? goalMarkerOptions.minBottomPadding
      : 16

  normalizedPadding.top = Math.max(normalizedPadding.top || 0, minTopPadding)
  normalizedPadding.bottom = Math.max(
    normalizedPadding.bottom || 0,
    minBottomPadding
  )

  return normalizedPadding
}

// Resolve o plugin de marcadores de meta (dados.goals) e o layout.padding
// ajustado pra acomodar os ícones — retorna null quando a feature não se
// aplica (sem goals, applyGoalMarkers desligado, ou plugin vazio).
const resolveGoalMarkersForLines = (chartOptions, dados) => {
  if (!hasUsableGoals(dados)) return null

  const goalMarkerOptions = dados.goalMarkerOptions || {}
  const goalPlugin = createGoalMarkersPlugin({
    goals: dados.goals,
    ...goalMarkerOptions,
  })

  if (!goalPlugin || !Object.keys(goalPlugin).length) return null

  const normalizedPadding = computeGoalMarkersPadding(
    chartOptions.layout,
    goalMarkerOptions
  )

  return {
    layout: { ...chartOptions.layout, padding: normalizedPadding },
    plugin: goalPlugin,
  }
}

/**
 * Formatação de dados para o grafico de linha da biblioteca ChartJS.
 * @param {Object} dados Dados para formatar os valores para o grafico.
 *  mode: Modo de apresentação dos dados. [%, R$]. Default: R$
 *  formatData: Funcao para formatar cada valor numerico. Opcional.
 *  data: Lista de datasets
 *  applyGoalMarkers: boolean (opcional). Default: true. Controla se a lógica de metas
 *    (dados.goals) será aplicada ao gráfico de linhas.
 *    label: Nome do grupo de dados
 *    dados: Lista de valores numericos
 * @returns {Object} Objetos necessarios para apresentar o grafico.
 *  chartOptions
 *  chartData
 */
export const formatLines = (dados, legendEndOfLine) => {
  const Colors = store.getters['parameters/colors'] || {}

  const chartOptions = buildLinesChartOptions(dados, legendEndOfLine)
  const datasets = buildLinesDatasets(dados, Colors)
  const chartData = { labels: dados.labels, datasets }
  const chartPlugins = legendEndOfLine ? [getLegendEndOfLinePlugin()] : []

  if (legendEndOfLine) {
    chartOptions.layout = buildLegendEndOfLineLayout(chartOptions, datasets)
  }

  const goalMarkers = resolveGoalMarkersForLines(chartOptions, dados)
  if (goalMarkers) {
    chartOptions.layout = goalMarkers.layout
    chartPlugins.push(goalMarkers.plugin)
  }

  return { chartOptions, chartData, chartPlugins }
}

/**
 * Formatação de dados para o grafico de barras da biblioteca ChartJS.
 * @param {Object} dados Dados para formatar os valores para o grafico.
 *  mode: Modo de apresentação dos dados. [%, R$]. Default: R$
 *  formatData: Funcao para formatar cada valor numerico. Opcional
 *  data: Lista de datasets
 *    label: Nome do grupo de dados.
 *    dados: Lista dos valores numericos.
 * @returns {Object} Objetos necessarios para apresentar o grafico.
 *  chartOptions
 *  chartData
 */
export const formatBars = (dados) => {
  const Colors = store.getters['parameters/colors'] || {}

  const { formatValuesY, funcFormatData, formatValuesTooltip } =
    optionsFormatData(dados.mode)

  const formatData = dados.formatData || funcFormatData
  const chartOptions = {
    scales: {
      yAxes: [
        {
          id: 'first-y-axis',
          position: 'left',
          ticks: {
            display: true,
            beginAtZero: true,
            callback: (v, i, vs) => {
              return formatValuesY(formatData(v))
            },
          },
          gridLines: {
            display: true,
            color: GRID_COLOR,
            zeroLineColor: GRID_COLOR,
            drawBorder: false,
          },
          // stacked: dados.stacked,
        },
        // {
        //   id: 'line-axis',
        //   position: 'right',
        //   ticks: {
        //     display: true,
        //     beginAtZero: true,
        //     callback: (v, i, vs) => {
        //       return formatValuesY(formatData(v))
        //     },
        //   },
        //   gridLines: {
        //     display: true,
        //   },
        //   stacked: false,
        // },
      ],
      xAxes: [
        {
          ticks: {
            beginAtZero: true,
          },
          gridLines: {
            display: false,
          },
          // stacked: dados.stacked,
        },
      ],
    },
    legend: {
      display: true,
      position: 'bottom',
      labels: {
        boxWidth: LEGEND_BOX_WIDTH,
        padding: 12,
        fontSize: 11,
      },
    },
    tooltips: {
      enabled: true,
      mode: 'index',
      callbacks: withTooltipDefaults({
        label: function (tooltipItems, data) {
          return `${
            data.datasets[tooltipItems.datasetIndex].label
          }: ${formatValuesTooltip(formatData(tooltipItems.yLabel))}`
        },
      }),
    },
    responsive: true,
    maintainAspectRatio: false,
  }

  const datasets = []

  dados.data.forEach((item) => {
    datasets.push({
      label: item.label,
      name: item.label,
      type: item.type || 'bar',
      order: item.type === 'line' ? -1 : 1,
      backgroundColor: item.color || Colors.primary,
      fill: false,
      borderColor: item.color || Colors.primary,
      data: item.dados || [],
      barPercentage: 0.5,
      categoryPercentage: 1,
      // stack: item.type === 'line',
      // ...(item.chartOptions || {}),
      // yAxisID: item.type === 'line' ? 'line-axis' : 'first-y-axis',
    })
  })

  const chartData = {
    labels: dados.labels,
    datasets: datasets,
  }

  return { chartOptions, chartData }
}

/**
 * Para novo BI v2
 * Formatação de dados para o grafico de barras da biblioteca ChartJS.
 * @param {Object} dados Dados para formatar os valores para o grafico.
 *  mode: Modo de apresentação dos dados. [%, R$]. Default: R$
 *  formatData: Funcao para formatar cada valor numerico. Opcional
 *  data: Lista de datasets
 *    label: Nome do grupo de dados.
 *    dados: Lista dos valores numericos.
 * @returns {Object} Objetos necessarios para apresentar o grafico.
 *  chartOptions
 *  chartData
 */
// eslint-disable-next-line max-statements
export const formatBars_v2 = (dados, legendEndOfLine) => {
  const Colors = store.getters['parameters/colors'] || {}

  const { formatValuesY, funcFormatData } = optionsFormatData(
    dados.mode,
    dados.currency
  )

  const hasRightAxis = dados.data.find((item) => item.axis === 'right-axis')
  const showLegend = typeof dados.legend === 'boolean' ? dados.legend : true

  const formatData = dados.formatData || funcFormatData

  // Bordas levemente arredondadas no topo das barras (Novo Design 2026). Lido
  // pelo override global de Rectangle em plugins/chartjsRounded.js via
  // options.cornerRadius — mesmo padrão do gráfico de barras horizontais.
  const CORNER_RADIUS = 4
  const chartOptions = {
    cornerRadius: CORNER_RADIUS,
    scales: {
      yAxes: [
        {
          id: 'left-axis',
          position: 'left',
          ticks: {
            display: true,
            beginAtZero: true,
            fontSize: 12,
            fontColor: AXIS_FONT_COLOR,
            fontStyle: 'normal',
            fontFamily: AXIS_FONT_FAMILY,
            callback: (v, i, vs) => {
              if (v === 0) return '0'
              return formatValuesY(formatData(v))
            },
          },
          gridLines: {
            display: true,
            color: GRID_COLOR,
            zeroLineColor: GRID_COLOR,
            drawBorder: false,
          },
          // stacked: dados.stacked,
        },
        hasRightAxis && {
          id: 'right-axis',
          position: 'right',
          ticks: {
            display: true,
            beginAtZero: true,
            fontSize: 12,
            fontColor: AXIS_FONT_COLOR,
            fontStyle: 'normal',
            fontFamily: AXIS_FONT_FAMILY,
            callback: (v, i, vs) => {
              return optionsFormatData(
                hasRightAxis.modeRightAxis || 'R$',
                dados.currency
              ).formatValuesY(formatData(v))
              // return formatValuesY(formatData(v))
            },
          },
          gridLines: {
            display: true,
            drawOnChartArea: false, // only draw grid lines for one axis
          },
          // stacked: dados.stacked,
        },
      ].filter((axis) => !!axis),
      xAxes: [
        {
          ticks: {
            beginAtZero: true,
            fontSize: 12,
            fontColor: AXIS_FONT_COLOR,
            fontStyle: 'normal',
            fontFamily: AXIS_FONT_FAMILY,
          },
          gridLines: {
            display: false,
          },
          // stacked: dados.stacked,
        },
      ],
    },
    legend: {
      display: showLegend,
      position: 'bottom',
      align: 'start',
      onClick: function (click, legendItem, legend) {
        if (Chart) {
          Chart.defaults.global.legend.onClick.call(this, click, legendItem)
        } else {
          console.error('Chart is not defined')
        }
      },
      labels: {
        pointStyle: 'rect',
        usePointStyle: false,
        boxWidth: LEGEND_BOX_WIDTH,
        padding: 12,
        fontSize: 11,
        generateLabels: (chart) => {
          return chart.data.datasets.map((dataset, index) => {
            return {
              text: dataset.label,
              fillStyle: dataset.backgroundColor,
              strokeStyle: dataset.backgroundColor,
              pointStyle:
                dataset.pointStyle ||
                (dataset.type === 'line' ? 'line' : 'rect'),
              hidden: !chart.isDatasetVisible(index),
              datasetIndex: index,
            }
          })
        },
      },
    },
    tooltips: {
      enabled: true,
      mode: 'index',
      intersect: false,
      callbacks: withTooltipDefaults({
        ...(dados.fullLabels
          ? {
              title: function (tooltipItems) {
                if (!tooltipItems || !tooltipItems.length) return ''
                const first = tooltipItems[0]
                if (!first) return ''
                return dados.fullLabels[first.index] || first.xLabel || ''
              },
            }
          : {}),
        label: function (tooltipItems, data) {
          const dataset = data.datasets[tooltipItems.datasetIndex]
          const value = optionsFormatData(
            dataset.modeRightAxis || dataset.mode || dados.mode || 'R$',
            dados.currency
          ).formatValuesTooltip(tooltipItems.yLabel)
          if (
            dataset.percentages &&
            dataset.percentages[tooltipItems.index] != null
          ) {
            const pct = toDecPercent(dataset.percentages[tooltipItems.index])
            return `${dataset.label}: ${pct} - ${value}`
          }
          return `${dataset.label}: ${value}`
        },
      }),
    },
    responsive: true,
    maintainAspectRatio: false,
  }

  const datasets = []

  dados.data.forEach((item) => {
    const objchart = {
      label: item.label,
      name: item.label,
      type: item.type || 'bar',
      order: item.type === 'line' ? -1 : 1,
      backgroundColor: item.color || Colors.primary,
      fill: false,
      borderColor: item.color || Colors.primary,
      data: item.dados || [],
      barPercentage: 0.5,
      categoryPercentage: 1,
      stack: item.type !== 'line',
      borderWidth: 3,
      yAxisID: item.axis || 'left-axis',
      ...(item.chartOptions || {}),
      mode: item.modeRightAxis || item.mode || dados.mode,
      percentages: item.percentages || null,
      // yAxisID: item.type === 'line' ? 'line-axis' : 'first-y-axis',
    }
    datasets.push(objchart)
  })

  const chartData = {
    labels: dados.labels,
    datasets: datasets,
  }

  if (dados.datalabels) {
    chartOptions.plugins = {
      ...chartOptions.plugins,
      datalabels: dados.datalabels,
    }
  }

  if (dados.layout) {
    chartOptions.layout = dados.layout
  }

  const chartPlugins = legendEndOfLine ? [getLegendEndOfLinePlugin()] : []
  if (dados.datalabels) {
    chartPlugins.push(ChartDataLabels)
  }

  return { chartOptions, chartData, chartPlugins }
}

/**
 * Formatação de dados para o grafico de barras na horizontal da biblioteca ChartJS.
 * @param {Object} dados Dados para formatar os valores para o grafico.
 *  mode: Modo de apresentação dos dados. [%, R$]. Default: R$
 *  formatData: Funcao para formatar cada valor numerico. Opcional
 *  data: Lista de datasets
 *    label: Nome do grupo de dados.
 *    dados: Lista dos valores numericos.
 * @returns {Object} Objetos necessarios para apresentar o grafico.
 *  chartOptions
 *  chartData
 */
export const formatBarsHorizontal = (dados) => {
  const Colors = store.getters['parameters/colors'] || {}
  const { formatValuesY, funcFormatData, formatValuesTooltip } =
    optionsFormatData(dados.mode)
  const formatData = dados.formatData || funcFormatData

  const chartOptions = {
    scales: {
      yAxes: [
        {
          id: 'first-y-axis',
          position: 'left',
          ticks: {
            display: true,
            beginAtZero: true,
            callback: (v, i, vs) => {
              return formatValuesY(formatData(v))
            },
          },
          gridLines: {
            display: false,
          },
          stacked: true,
        },
      ],
      xAxes: [
        {
          ticks: {
            beginAtZero: true,
          },
          gridLines: {
            display: false,
          },
          stacked: true,
        },
      ],
    },
    legend: {
      display: true,
      position: 'bottom',
    },
    elements: {
      point: {
        radius: 2,
        borderWidth: 0,
        style: 'star',
      },
      line: {
        borderWidth: 0,
      },
    },
    tooltips: {
      enabled: true,
      mode: 'index',
      callbacks: withTooltipDefaults({
        label: function (tooltipItems, data) {
          return formatValuesTooltip(formatData(tooltipItems.yLabel))
        },
      }),
    },
    plugins: {
      datalabels: {
        display: true,
        color: '#FFF',
      },
    },
    responsive: true,
    maintainAspectRatio: false,
  }

  const datasets = []

  dados.data.forEach((item) => {
    datasets.push({
      label: item.label,
      name: item.label,
      type: 'bar',
      order: 1,
      backgroundColor: item.color || Colors.primary,
      borderWidth: 1,
      data: item.dados,
      maxBarThickness: 40,
    })
  })

  const chartData = {
    labels: dados.labels,
    datasets: datasets,
  }

  return { chartOptions, chartData }
}

/**
 * Formatação de dados para o grafico de barras na horizontal da biblioteca ChartJS V2  .
 * @param {Object} dados Dados para formatar os valores para o grafico.
 *  mode: Modo de apresentação dos dados. [%, R$]. Default: R$
 *  formatData: Funcao para formatar cada valor numerico. Opcional
 *  data: Lista de datasets
 *    label: Nome do grupo de dados.
 *    dados: Lista dos valores numericos.
 * @returns {Object} Objetos necessarios para apresentar o grafico.
 *  chartOptions
 *  chartData
 */
export const formatBarsHorizontal_v2 = (dados, showLabelEndOfBar) => {
  const { funcFormatData } = optionsFormatData(dados.mode, dados.currency)
  const formatData = dados.formatData || funcFormatData

  const LEFT_EXTRA = 10
  const showLegend = typeof dados.legend === 'boolean' ? dados.legend : true

  const formatPct = (p) =>
    p == null ? null : p >= 0 && p <= 1 ? toDecPercent(p) : toPercent(p)

  const CORNER_RADIUS = 4
  const chartOptions = {
    cornerRadius: CORNER_RADIUS,
    layout: { padding: { right: 21, bottom: 0 } },
    plugins: { datalabels: { display: true, color: '#FFF' } },
    tooltips: {
      enabled: true,
      mode: 'y',
      intersect: false,
      callbacks: withTooltipDefaults({
        title: (items) => {
          const first = items && items.length ? items[0] : null
          if (!first) return ''
          if (first.yLabel != null) return first.yLabel
          if (first.label != null) return first.label
          return ''
        },
        label: (tooltipItem, data) => {
          const ds = data.datasets[tooltipItem.datasetIndex] || {}
          const { formatValuesTooltip } = optionsFormatData(
            ds.mode || dados.mode || 'R$',
            dados.currency
          )
          const displayVal = Array.isArray(ds.rawValues)
            ? ds.rawValues[tooltipItem.index]
            : tooltipItem.xLabel
          const valueText = formatValuesTooltip(formatData(displayVal))
          const pctVal = Array.isArray(ds.porcentagens)
            ? ds.porcentagens[tooltipItem.index]
            : null
          const pctText = formatPct(pctVal)
          return pctText
            ? `${ds.label}: ${pctText} - ${valueText}`
            : `${ds.label}: ${valueText}`
        },
      }),
    },
    scales: {
      yAxes: [
        {
          afterFit(scale) {
            scale.width = scale.width + LEFT_EXTRA
          },
          ticks: {
            fontSize: 14,
            fontColor: AXIS_FONT_COLOR,
            fontStyle: 'normal',
            fontFamily: AXIS_FONT_FAMILY,
            padding: 20,
          },
          stacked: false,
          gridLines: { display: false },
        },
      ],
      xAxes: [
        {
          dataLabels: { display: false },
          stacked: false,
          gridLines: { display: !showLabelEndOfBar },
          ticks: {
            display: !showLabelEndOfBar,
            beginAtZero: true,
            fontSize: 14,
            fontColor: AXIS_FONT_COLOR,
            fontStyle: 'normal',
            fontFamily: AXIS_FONT_FAMILY,
            callback: (v) =>
              optionsFormatData('monetario_abreviado', dados.currency).formatValuesY(v),
          },
        },
      ],
    },
    responsive: true,
    maintainAspectRatio: false,
    elements: {
      point: { radius: 2, borderWidth: 0 },
      line: { borderWidth: 0 },
    },
    legend: {
      display: showLegend,
      position: 'bottom',
      align: 'start',
      labels: {
        pointStyle: 'rect',
        usePointStyle: false,
        boxWidth: LEGEND_BOX_WIDTH,
        padding: 12,
        fontSize: 11,
        generateLabels: (chart) => {
          return chart.data.datasets.map((dataset, index) => {
            const bg = Array.isArray(dataset.backgroundColor)
              ? dataset.backgroundColor[0]
              : dataset.backgroundColor
            return {
              text: dataset.label,
              fillStyle: bg,
              strokeStyle: bg,
              pointStyle: dataset.pointStyle || 'rect',
              hidden: !chart.isDatasetVisible(index),
              datasetIndex: index,
            }
          })
        },
      },
    },
  }
  const { labels, datasets } = dados.data

  const normalizeColors = (len, colors, fallback) => {
    if (Array.isArray(colors) && colors.length) {
      if (colors.length >= len) return colors.slice(0, len)
      const out = colors.slice()
      while (out.length < len) out.push(fallback)
      return out
    }
    return Array(len).fill(fallback)
  }

  const formattedDatasets = Object.entries(datasets).map(
    ([key, value], index) => {
      const baseColor = value.color || paleta[index] || getRandomColor()
      const dataArr = value.dados || []
      const perBarColors = normalizeColors(
        dataArr.length,
        value.colors,
        baseColor
      )
      return {
        label: key,
        data: dataArr,
        backgroundColor: perBarColors,
        borderColor: perBarColors,
        maxBarThickness: 24,
        labelColor: value.labelColor || '#fff',
        porcentagens: value.porcentagens || null,
        rawValues: value.rawValues || null,
        mode: value.mode || dados.mode,
      }
    }
  )

  Object.assign(chartOptions, {
    __dadosMode: dados.mode,
    __formatData: formatData,
    __currency: dados.currency,
  })

  const chartData = { labels, datasets: formattedDatasets }
  const chartPlugins = showLabelEndOfBar ? [getBarValueLabelsPlugin()] : []

  return { chartOptions, chartData, chartPlugins }
}

/**
 * Formatação de dados para o grafico de donut da biblioteca ChartJS.
 * @param {Object} dados Dados para formatar os valores para o grafico.
 *  mode: Modo de apresentação dos dados. [%, R$].
 *  formatData: Funcao para formatar cada valor numerico. Opcional
 *  labels: Textos com nome de cada parte.
 *  data: Valores numericos de cada parte.
 * @returns {Object} Objetos necessarios para apresentar o grafico.
 *  chartOptions
 *  chartData
 */
export const formatDonut = (dados, modeChartJs) => {
  const { funcFormatData, formatValuesTooltip } = optionsFormatData(dados.mode)
  const formatData = dados.formatData || funcFormatData
  const chartOptions = {
    colors:
      dados.colors || _paleta || store.getters['parameters/paleta'] || paleta,
    legend: {
      show: true,
      position: 'bottom',
    },
    dataLabels: {
      enabled: true,
    },
    tooltip: {
      y: {
        formatter: function (value) {
          return formatValuesTooltip(formatData(value))
        },
      },
    },
    labels: dados.labels,
    stroke: {
      width: 0,
    },
    plotOptions: {
      pie: {
        stroke: {
          show: false,
        },
        donut: {
          size: '80%',
          labels: {
            show: false,
            name: {
              fontSize: '12px',
              color: '#8e8e8e',
              offsetY: -15,
            },
            value: {
              fontSize: '12px',
              fontFamily: 'ProximaNova',
              offsetY: -15,
            },
            total: {
              show: false,
            },
          },
        },
      },
    },
  }

  let chartData

  if (modeChartJs) {
    const totalObj =
      Object.values(dados).find(
        (item) => typeof item === 'object' && 'total' in item
      ) || {}

    const dataset = {
      data: Object.values(dados).map((item) => item.percent || item.value || 0),
      backgroundColor: Object.values(dados).map(
        (item) => item.color || getRandomColor()
      ),
      borderWidth: 4,
      total: totalObj.total || 0,
    }

    chartData = {
      labels: Object.values(dados).map((item) =>
        item.percent ? `${item.name} (${toPercent(item.percent)})` : item.name
      ),
      datasets: [dataset],
    }
  } else {
    chartData = dados.data
  }

  return { chartOptions, chartData }
}

// ------------------------- Funções auxiliares Donut V2 -------------------------
const generateColors = (labels, userBg) => {
  return labels.map((_, i) => userBg[i] || _paleta[i % _paleta.length])
}

const createDataset = (values, colors, showTotal, ds0, totalSum) => {
  const dataset = {
    data: values,
    backgroundColor: colors,
    borderWidth: 0,
  }

  if (showTotal) {
    dataset.total = ds0.total || totalSum
  }

  return dataset
}

const generateLegendLabels = (chart) => {
  const data = chart.data || {}
  const ds = (data.datasets && data.datasets[0]) || {
    data: [],
    backgroundColor: [],
  }
  const vals = (ds.data || []).map((n) => Number(n) || 0)
  const sum = vals.reduce((acc, val) => acc + val, 0)
  const meta = chart.getDatasetMeta(0)
  const bgs = Array.isArray(ds.backgroundColor) ? ds.backgroundColor : []

  return (data.labels || []).map((lbl, i) => {
    const v = vals[i] || 0
    const p = sum > 0 ? v / sum : 0
    const pctText = toDecPercent(p)

    return {
      text: `${lbl} - ${pctText}`,
      fillStyle: bgs[i] || _paleta[i % _paleta.length],
      strokeStyle: bgs[i] || _paleta[i % _paleta.length],
      hidden: meta.data && meta.data[i] ? meta.data[i].hidden : false,
      index: i,
    }
  })
}

const createTooltipLabel = (
  tt,
  data,
  values,
  totalSum,
  mode,
  formatValuesTooltip,
  formatData
) => {
  const i = tt.index
  const label = data.labels[i]
  const v = values[i]
  const p = totalSum > 0 ? v / totalSum : 0

  if (mode === '%') {
    const norm = v > 1.0001 ? v / 100 : v
    return `${label}: ${toDecPercent(norm)}`
  }

  const absTxt = formatValuesTooltip(formatData(v))
  return `${label}: ${absTxt} (${toDecPercent(p)})`
}

const createLegendLabelsConfig = (showLegendPct) => {
  const legendLabels = {
    usePointStyle: false,
    boxWidth: LEGEND_BOX_WIDTH,
    padding: 12,
    fontSize: 11,
  }

  if (showLegendPct) {
    legendLabels.generateLabels = generateLegendLabels
  }

  return legendLabels
}

const processDonutData = (donut) => {
  const ds0 = (donut.datasets && donut.datasets[0]) || {
    data: [],
    backgroundColor: [],
  }
  const labels = donut.labels || []
  const values = (ds0.data || []).map((n) => Number(n) || 0)
  const totalSum = values.reduce((acc, val) => acc + val, 0)
  const userBg = ds0.backgroundColor || []

  return { ds0, labels, values, totalSum, userBg }
}

const createChartOptions = (
  donut,
  showTotal,
  showLegendPct,
  values,
  totalSum,
  mode,
  formatValuesTooltip,
  formatData
) => {
  const cutout = donut.cutoutPercentage || 50
  const legendLabels = createLegendLabelsConfig(showLegendPct)

  return {
    showTotal: showTotal,
    removeCurrencyTotalFor: donut.removeCurrencyTotalFor || false,
    responsive: true,
    maintainAspectRatio: false,
    cutoutPercentage: cutout,
    legend: {
      display: true,
      position: 'right',
      labels: legendLabels,
    },
    tooltips: {
      callbacks: withTooltipDefaults({
        label: (tt, data) =>
          createTooltipLabel(
            tt,
            data,
            values,
            totalSum,
            mode,
            formatValuesTooltip,
            formatData
          ),
      }),
    },
  }
}

/**
 * Formatação de dados para o gráfico de donut da biblioteca ChartJS (versão 2).
 *
 * @param {Object} donut - Dados para formatar os valores para o gráfico.
 * @param {string} [donut.mode='R$'] - Modo de apresentação dos dados [%, R$, $].
 * @param {string[]} [donut.labels=[]] - Textos com nome de cada parte.
 * @param {Object[]} [donut.datasets] - Datasets do gráfico. Usa apenas o primeiro.
 * @param {number[]} [donut.datasets[0].data=[]] - Valores numéricos de cada parte.
 * @param {string[]} [donut.datasets[0].backgroundColor=[]] - Cores customizadas. Usa paleta padrão se vazio.
 * @param {number} [donut.cutoutPercentage=50] - Percentual do corte interno (0-100).
 * @param {boolean} [showTotal=false] - Adiciona total ao dataset para exibição no centro.
 * @param {boolean} [showLegendPct=false] - Exibe percentuais na legenda.
 *
 * @returns {Object} Objetos necessários para apresentar o gráfico.
 * @returns {Object} return.chartOptions - Configurações do gráfico.
 * @returns {Object} return.chartData - Dados formatados para o ChartJS.
 *
 * @example
 * const data = { labels: ['A', 'B'], datasets: [{ data: [100, 200] }] }
 * const result = formatDonut_v2(data, true, true)
 */
export const formatDonut_v2 = (donut, showTotal, showLegendPct) => {
  const mode = donut.mode || 'R$'
  const { funcFormatData, formatValuesTooltip } = optionsFormatData(mode)
  const formatData = donut.formatData || funcFormatData

  const { ds0, labels, values, totalSum, userBg } = processDonutData(donut)
  const colors = generateColors(labels, userBg)
  const dataset = createDataset(values, colors, showTotal, ds0, totalSum)
  const chartData = { labels, datasets: [dataset] }
  const chartOptions = createChartOptions(
    donut,
    showTotal,
    showLegendPct,
    values,
    totalSum,
    mode,
    formatValuesTooltip,
    formatData
  )

  return { chartOptions, chartData }
}

/**
 * Formatação de dados para o grafico de barras na horizontal da biblioteca ChartJS.
 * @param {Object} dados Dados para formatar os valores para o grafico.
 *  mode: Modo de apresentação dos dados. [%, R$]. Default: R$
 *  formatData: Funcao para formatar cada valor numerico. Opcional
 *  data: Lista de datasets
 *    label: Nome do grupo de dados.
 *    dados: Lista dos valores numericos.
 * @returns {Object} Objetos necessarios para apresentar o grafico.
 *  chartOptions
 *  chartData
 */
export const formatRadarMultiple = (dados) => {
  const Colors = store.getters['parameters/colors'] || {}

  const { funcFormatData, formatValuesTooltip } = optionsFormatData(dados.mode)

  const formatData = dados.formatData || funcFormatData

  const chartOptions = {
    scale: {
      ticks: {
        beginAtZero: true,
        stepSize: dados.stepSize || 1,
      },
    },
    legend: {
      display: true,
      position: 'bottom',
      labels: {
        usePointStyle: true,
        boxWidth: 12,
      },
    },
    elements: {
      line: {
        borderWidth: 0,
      },
    },
    tooltips: {
      enabled: true,
      mode: 'index',
      callbacks: withTooltipDefaults({
        label: function (tooltipItems, data) {
          if (dados.mode && dados.mode.length) {
            return formatValuesTooltip(formatData(tooltipItems.yLabel))
          }

          return tooltipItems.yLabel
        },
      }),
    },
    plugins: {
      datalabels: {
        display: true,
        color: '#FFF',
      },
    },
    responsive: true,
    maintainAspectRatio: false,
  }

  const datasets = []

  const safeData = Array.isArray(dados.data) ? dados.data : []
  safeData.forEach((item) => {
    datasets.push({
      label: item.label,
      name: item.label,
      type: 'radar',
      order: 1,
      backgroundColor: item.color || Colors.primary,
      borderColor: item.color || Colors.primary,
      borderWidth: 2,
      data: item.dados,
    })
  })

  const chartData = {
    labels: dados.labels,
    datasets: datasets,
  }

  return { chartOptions, chartData }
}
